import { Button } from '@/components/ui/Button';
import { TimePicker } from '@/components/ui/TimePicker';
import {
  useVaultSyncConfiguration,
  useSaveVaultSyncConfiguration,
} from '@/hooks/useVaultSyncConfiguration';
import { useEmployees } from '@/features/employees/hooks/useEmployees';
import { vaultService } from '@/services/vault.service';
import { Vault, VaultStatus } from '@/types/vault.types';
import {
  DEFAULT_VAULT_SYNC_CONFIG,
  VaultSyncConfigurationFormState,
} from '@/types/vault-sync-configuration.types';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  FileBarChart,
  Save,
  Loader2,
  Building2,
  Users,
  CheckCircle,
  Clock,
  AlertTriangle,
  ArrowRightLeft,
  UserCheck,
} from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface EmployeeVaultInfo {
  vault: Vault;
  employeeName: string;
  employeeMatricule: string;
}

interface VaultAutoDepositTabProps {
  folderUuid: string;
  folderName: string;
  domainUuid?: string;
  folderVault: Vault | null | undefined;
}

const DAYS = Array.from({ length: 28 }, (_, i) => ({
  value: (i + 1).toString(),
  label: (i + 1).toString(),
}));

export function VaultAutoDepositTab({
  folderUuid,
  folderName: _folderName,
  domainUuid,
  folderVault: _folderVault,
}: VaultAutoDepositTabProps) {
  const { t } = useTranslation();

  // Fetch existing configuration
  const { data: existingConfig, isLoading: isLoadingConfig } =
    useVaultSyncConfiguration(folderUuid);
  const { mutate: saveConfig, isPending: isSavingConfig } =
    useSaveVaultSyncConfiguration();

  // Auto sync config state
  const [formState, setFormState] = useState<VaultSyncConfigurationFormState>(
    DEFAULT_VAULT_SYNC_CONFIG
  );

  // Load existing config into form state
  useEffect(() => {
    if (existingConfig) {
      setFormState({
        isActive: existingConfig.isActive,
        syncPayslips: existingConfig.syncPayslips,
        payslipDayOfMonth: existingConfig.payslipDayOfMonth,
        payslipExecutionHour: existingConfig.payslipExecutionHour,
        payslipExecutionMinute: existingConfig.payslipExecutionMinute,
        syncDsn: existingConfig.syncDsn,
        dsnDayOfMonth: existingConfig.dsnDayOfMonth,
        dsnExecutionHour: existingConfig.dsnExecutionHour,
        dsnExecutionMinute: existingConfig.dsnExecutionMinute,
        syncToFolderVault: existingConfig.syncToFolderVault,
        syncToEmployeeVaults: existingConfig.syncToEmployeeVaults,
        directDepositToEmployee: existingConfig.directDepositToEmployee ?? true,
        usePreviousMonth: existingConfig.usePreviousMonth,
        notificationEmail: existingConfig.notificationEmail || '',
      });
    }
  }, [existingConfig]);

  // Fetch employees
  const { data: employeesData } = useEmployees(folderUuid, { limit: 500 });

  // Fetch employee vaults with their status
  const { data: employeeVaultsMap } = useQuery({
    queryKey: ['vault', 'folder-employee-vaults-full', folderUuid],
    queryFn: async () => {
      try {
        const response = await vaultService.getFolderEmployeeVaults(folderUuid);
        const data = response?.data;
        const vaults = Array.isArray(data) ? data : [];
        const vaultMap = new Map<string, EmployeeVaultInfo>();
        vaults.forEach((v) => {
          if (v.vault?.entityId) {
            vaultMap.set(v.vault.entityId, v);
          }
        });
        return vaultMap;
      } catch (error) {
        console.error('Error fetching employee vaults:', error);
        return new Map<string, EmployeeVaultInfo>();
      }
    },
    enabled: !!folderUuid,
  });

  // Count employees with active vaults
  const vaultStats = useMemo(() => {
    const employees = employeesData?.data || [];
    let activeCount = 0;
    let pendingCount = 0;
    let noVaultCount = 0;

    employees.forEach((emp) => {
      const vaultInfo = employeeVaultsMap?.get(emp.uuid);
      if (!vaultInfo) {
        noVaultCount++;
      } else if (vaultInfo.vault?.status === VaultStatus.ACTIVE) {
        activeCount++;
      } else {
        pendingCount++;
      }
    });

    return { activeCount, pendingCount, noVaultCount, total: employees.length };
  }, [employeesData?.data, employeeVaultsMap]);

  const handleSaveAutoSyncConfig = async () => {
    // Only validate document and destination selection when enabling auto-sync
    if (formState.isActive) {
      if (!formState.syncPayslips && !formState.syncDsn) {
        toast.error(
          t(
            'vault.sync.selectDocument',
            'Veuillez sélectionner au moins un type de document'
          )
        );
        return;
      }

      if (!formState.syncToFolderVault && !formState.syncToEmployeeVaults) {
        toast.error(
          t(
            'vault.sync.selectDestination',
            'Veuillez sélectionner au moins une destination'
          )
        );
        return;
      }
    }

    if (!domainUuid) {
      toast.error('Domain UUID is required');
      return;
    }

    saveConfig({
      folderUuid,
      domainUuid,
      ...formState,
    });
  };

  const updateFormField = <K extends keyof VaultSyncConfigurationFormState>(
    field: K,
    value: VaultSyncConfigurationFormState[K]
  ) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  if (isLoadingConfig) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100">
            <Clock className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              {t('vault.autoDeposit.title', 'Dépôt Automatique')}
            </h3>
            <p className="text-sm text-neutral-500">
              {t(
                'vault.autoDeposit.description',
                'Configurez le dépôt automatique des documents vers les coffres-forts'
              )}
            </p>
          </div>
        </div>
        <label className="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            checked={formState.isActive}
            onChange={e => updateFormField('isActive', e.target.checked)}
            className="peer sr-only"
          />
          <div className="peer h-6 w-11 rounded-full bg-neutral-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-neutral-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-indigo-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300"></div>
        </label>
      </div>

      {/* [2026-05-13] Bandeau d'information : opt-in explicite par dossier.
          Évite la confusion vécue par les clients qui pensaient que le dépôt
          auto était activé partout par défaut. */}
      {!formState.isActive && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
            <p className="text-sm text-blue-800">
              {t(
                'vault.autoDeposit.disabledByDefault',
                'Le dépôt automatique est désactivé pour ce dossier. Aucun document ne sera déposé tant que vous n\'aurez pas activé l\'interrupteur ci-dessus puis enregistré la configuration.'
              )}
            </p>
          </div>
        </div>
      )}

      <div
        className={`space-y-6 ${!formState.isActive ? 'pointer-events-none opacity-50' : ''}`}
      >
        {/* Bulletin Configuration */}
        <div className="rounded-lg border border-neutral-200 p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              <span className="font-medium text-neutral-900">
                {t('vault.sync.payslipsConfig', 'Configuration Bulletins')}
              </span>
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={formState.syncPayslips}
                onChange={e =>
                  updateFormField('syncPayslips', e.target.checked)
                }
                className="peer sr-only"
              />
              <div className="peer h-5 w-9 rounded-full bg-neutral-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-neutral-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-500 peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
            </label>
          </div>

          {formState.syncPayslips && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-700">
                    {t('vault.sync.dayOfMonth', 'Jour du mois')}
                  </label>
                </div>
                <select
                  value={formState.payslipDayOfMonth}
                  onChange={e =>
                    updateFormField(
                      'payslipDayOfMonth',
                      parseInt(e.target.value)
                    )
                  }
                  className="w-full rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                >
                  {DAYS.map(day => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>
              <TimePicker
                hour={formState.payslipExecutionHour}
                minute={formState.payslipExecutionMinute}
                onHourChange={hour =>
                  updateFormField('payslipExecutionHour', hour)
                }
                onMinuteChange={minute =>
                  updateFormField('payslipExecutionMinute', minute)
                }
                label={t('vault.sync.executionTime', "Heure d'exécution")}
              />
            </div>
          )}
        </div>

        {/* DSN Configuration */}
        <div className="rounded-lg border border-neutral-200 p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileBarChart className="h-5 w-5 text-green-500" />
              <span className="font-medium text-neutral-900">
                {t('vault.sync.dsnConfig', 'Configuration DSN')}
              </span>
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={formState.syncDsn}
                onChange={e => updateFormField('syncDsn', e.target.checked)}
                className="peer sr-only"
              />
              <div className="peer h-5 w-9 rounded-full bg-neutral-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-neutral-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
            </label>
          </div>

          {formState.syncDsn && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-700">
                    {t('vault.sync.dayOfMonth', 'Jour du mois')}
                  </label>
                </div>
                <select
                  value={formState.dsnDayOfMonth}
                  onChange={e =>
                    updateFormField('dsnDayOfMonth', parseInt(e.target.value))
                  }
                  className="w-full rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-green-500"
                >
                  {DAYS.map(day => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>
              <TimePicker
                hour={formState.dsnExecutionHour}
                minute={formState.dsnExecutionMinute}
                onHourChange={hour =>
                  updateFormField('dsnExecutionHour', hour)
                }
                onMinuteChange={minute =>
                  updateFormField('dsnExecutionMinute', minute)
                }
                label={t('vault.sync.executionTime', "Heure d'exécution")}
              />
            </div>
          )}
        </div>

        {/* Destinations */}
        <div>
          <label className="mb-3 block text-sm font-medium text-neutral-700">
            {t('vault.sync.destinations', 'Destinations')}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50">
              <input
                type="checkbox"
                checked={formState.syncToFolderVault}
                onChange={e =>
                  updateFormField('syncToFolderVault', e.target.checked)
                }
                className="h-4 w-4 rounded border-neutral-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-neutral-500" />
                <span className="text-sm font-medium text-neutral-900">
                  {t('vault.sync.folderVault', 'Coffre dossier')}
                </span>
              </div>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50">
              <input
                type="checkbox"
                checked={formState.syncToEmployeeVaults}
                onChange={e =>
                  updateFormField('syncToEmployeeVaults', e.target.checked)
                }
                className="h-4 w-4 rounded border-neutral-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-neutral-500" />
                <span className="text-sm font-medium text-neutral-900">
                  {t('vault.sync.employeeVaults', 'Coffres employés')}
                </span>
              </div>
            </label>
          </div>

          {/* Vault Status Stats */}
          {formState.syncToEmployeeVaults && vaultStats.total > 0 && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-success-500" />
                  <span className="text-neutral-600">
                    {t('vault.autoDeposit.activeVaults', '{{count}} actif(s)', { count: vaultStats.activeCount })}
                  </span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  <span className="text-neutral-600">
                    {t('vault.autoDeposit.pendingVaults', '{{count}} en attente', { count: vaultStats.pendingCount })}
                  </span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-neutral-300" />
                  <span className="text-neutral-600">
                    {t('vault.autoDeposit.noVaults', '{{count}} sans coffre', { count: vaultStats.noVaultCount })}
                  </span>
                </span>
              </div>

              {(vaultStats.pendingCount > 0 || vaultStats.noVaultCount > 0) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">
                        {t('vault.autoDeposit.inactiveVaultsWarning', 'Certains employés ne recevront pas leurs documents')}
                      </p>
                      <p className="mt-1 text-amber-700">
                        {t('vault.autoDeposit.inactiveVaultsHint', 'Seuls les coffres-forts actifs recevront les documents. Les employés avec des coffres en attente d\'activation ou sans coffre ne recevront pas leurs bulletins.')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Direct deposit toggle */}
        {formState.syncToEmployeeVaults && (
          <div className="rounded-lg border border-neutral-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100">
                  {formState.directDepositToEmployee ? (
                    <UserCheck className="h-4 w-4 text-purple-600" />
                  ) : (
                    <ArrowRightLeft className="h-4 w-4 text-purple-600" />
                  )}
                </div>
                <div>
                  <span className="block text-sm font-medium text-neutral-900">
                    {t('vault.autoDeposit.directDeposit', 'Dépôt direct aux salariés')}
                  </span>
                  <span className="block text-xs text-neutral-500">
                    {formState.directDepositToEmployee
                      ? t(
                          'vault.autoDeposit.directDepositOnDesc',
                          'Les bulletins sont envoyés directement dans les coffres des salariés'
                        )
                      : t(
                          'vault.autoDeposit.directDepositOffDesc',
                          'Les bulletins passent par le coffre employeur pour validation avant transfert aux salariés'
                        )}
                  </span>
                </div>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={formState.directDepositToEmployee}
                  onChange={e =>
                    updateFormField('directDepositToEmployee', e.target.checked)
                  }
                  className="peer sr-only"
                />
                <div className="peer h-5 w-9 rounded-full bg-neutral-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-neutral-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-purple-500 peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
              </label>
            </div>
            {!formState.directDepositToEmployee && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                  <p className="text-xs text-amber-800">
                    {t(
                      'vault.autoDeposit.employerReviewInfo',
                      'Les bulletins seront d\'abord déposés dans le coffre employeur. Vous pourrez ensuite les transférer manuellement vers les coffres des salariés depuis la liste des documents.'
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Period type */}
        <div>
          <label className="mb-3 block text-sm font-medium text-neutral-700">
            {t('vault.sync.periodType', 'Période à synchroniser')}
          </label>
          <div className="flex gap-3">
            <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50">
              <input
                type="radio"
                checked={!formState.usePreviousMonth}
                onChange={() => updateFormField('usePreviousMonth', false)}
                className="h-4 w-4 border-neutral-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-neutral-900">
                {t('vault.sync.currentMonth', 'Mois courant (N)')}
              </span>
            </label>
            <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50">
              <input
                type="radio"
                checked={formState.usePreviousMonth}
                onChange={() => updateFormField('usePreviousMonth', true)}
                className="h-4 w-4 border-neutral-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-neutral-900">
                {t('vault.sync.previousMonth', 'Mois précédent (N-1)')}
              </span>
            </label>
          </div>
        </div>

        {/* Notification email */}
        <div>
          <label className="mb-2 block text-sm font-medium text-neutral-700">
            {t('vault.sync.notificationEmail', 'Email de notification')}
          </label>
          <input
            type="email"
            value={formState.notificationEmail}
            onChange={e =>
              updateFormField('notificationEmail', e.target.value)
            }
            placeholder="email@exemple.com"
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500"
          />
          <p className="mt-1 text-xs text-neutral-500">
            {t(
              'vault.sync.notificationEmailInfo',
              'Recevoir une notification après chaque synchronisation'
            )}
          </p>
        </div>

      </div>

      {/* Save Button - Outside the disabled div so it's always clickable */}
      <Button
        onClick={handleSaveAutoSyncConfig}
        disabled={isSavingConfig}
        variant="outline"
        className="w-full"
      >
        {isSavingConfig ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('common.saving', 'Enregistrement...')}
          </>
        ) : (
          <>
            <Save className="mr-2 h-4 w-4" />
            {t('vault.sync.saveConfig', 'Enregistrer la Configuration')}
          </>
        )}
      </Button>

      {formState.isActive && (
        <div className="rounded-lg border border-success-200 bg-success-50 p-3">
          <div className="flex items-center gap-2 text-success-700">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm font-medium">
              {t(
                'vault.autoDeposit.active',
                'Dépôt automatique activé'
              )}
            </span>
          </div>
          <div className="mt-2 text-xs text-success-600">
            {formState.syncPayslips && (
              <p>
                • Bulletins : le {formState.payslipDayOfMonth} de chaque mois à{' '}
                {formState.payslipExecutionHour
                  .toString()
                  .padStart(2, '0')}
                :{formState.payslipExecutionMinute.toString().padStart(2, '0')}
              </p>
            )}
            {formState.syncDsn && (
              <p>
                • DSN : le {formState.dsnDayOfMonth} de chaque mois à{' '}
                {formState.dsnExecutionHour.toString().padStart(2, '0')}:
                {formState.dsnExecutionMinute.toString().padStart(2, '0')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
